import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Layers, Info } from 'lucide-react';

export const DashboardPreview: React.FC = () => {
  return (
    <section className="py-24 bg-secondaryBg overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-12">
           <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight mb-6">
                See Your Project in Action
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Our unified dashboard brings ownership, planning layers, and compliance rules into a single view. 
                Make data-driven decisions without switching tabs.
              </p>
           </div>
           <div className="hidden lg:block">
                {/* Spacer or additional info could go here, but keeping minimal */}
           </div>
        </div>

        {/* Dashboard Mockup Container */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="relative rounded-xl border border-gray-200 bg-white shadow-2xl overflow-hidden aspect-[16/10] md:aspect-[16/9]"
        >
            {/* Fake App Header */}
            <div className="h-12 border-b border-gray-100 flex items-center justify-between px-4 bg-white z-20 relative">
                <div className="flex items-center gap-4">
                    <div className="w-24 h-4 bg-gray-200 rounded"></div>
                    <div className="h-6 w-[1px] bg-gray-200"></div>
                    <div className="flex gap-2">
                         <div className="w-16 h-6 bg-green-50 rounded flex items-center justify-center text-[10px] font-medium text-green-600">Planning</div>
                         <div className="w-16 h-6 bg-gray-50 rounded flex items-center justify-center text-[10px] font-medium text-gray-500">Analysis</div>
                    </div>
                </div>
                <div className="flex gap-2">
                    <div className="w-8 h-8 rounded-full bg-gray-100"></div>
                </div>
            </div>

            <div className="flex h-full relative">
                {/* Sidebar */}
                <div className="w-64 border-r border-gray-100 bg-white hidden md:block p-4 z-20 h-[calc(100%-3rem)]">
                    <div className="mb-6">
                        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Layers</div>
                        <div className="space-y-2">
                            {[1,2,3].map(i => (
                                <div key={i} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded cursor-pointer">
                                    <div className="flex items-center gap-2">
                                        <Layers size={14} className="text-gray-500"/>
                                        <div className="w-20 h-3 bg-gray-200 rounded"></div>
                                    </div>
                                    <div className="w-3 h-3 border border-green-600 bg-green-600 rounded-[2px]"></div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div>
                         <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Properties</div>
                         <div className="space-y-3 bg-gray-50 p-3 rounded-lg border border-gray-100">
                             <div className="w-full h-3 bg-gray-200 rounded mb-1"></div>
                             <div className="w-3/4 h-3 bg-gray-200 rounded"></div>
                             <div className="w-1/2 h-3 bg-gray-200 rounded mt-2"></div>
                         </div>
                    </div>
                </div>

                {/* Main Map Area */}
                <div className="flex-1 relative bg-gray-50 overflow-hidden">
                    {/* Abstract Map Background */}
                    <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:20px_20px] opacity-50"></div>
                    
                    {/* Map Shapes */}
                    <div className="absolute top-1/4 left-1/3 w-48 h-48 border-2 border-gray-300 bg-white rounded-lg transform rotate-3 shadow-sm"></div>
                    <div className="absolute top-1/4 left-1/3 w-48 h-48 border-2 border-green-600 bg-green-600/10 rounded-lg transform translate-x-2 translate-y-2 rotate-3 z-10 flex items-center justify-center">
                        <MapPin className="text-green-600 drop-shadow-md" size={32} fill="currentColor" />
                    </div>
                    
                    {/* Floating Tooltip */}
                    <motion.div 
                        initial={{ opacity: 0, scale: 0.8 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.5 }}
                        className="absolute top-[20%] right-[20%] bg-white p-4 rounded-lg shadow-xl border border-gray-100 w-56 z-30"
                    >
                         <div className="flex justify-between items-start mb-2">
                             <div>
                                 <div className="text-sm font-bold text-gray-900">Final Plot #34</div>
                                 <div className="text-xs text-gray-500">Thaltej, Ahmedabad</div>
                             </div>
                             <Info size={14} className="text-gray-400"/>
                         </div>
                         <div className="grid grid-cols-2 gap-2 mt-3">
                             <div className="bg-green-50 p-2 rounded">
                                 <div className="text-[10px] text-green-600 font-semibold">FSI</div>
                                 <div className="text-sm font-bold text-green-700">2.7</div>
                             </div>
                             <div className="bg-green-50 p-2 rounded">
                                 <div className="text-[10px] text-green-600 font-semibold">Zone</div>
                                 <div className="text-sm font-bold text-green-700">R1</div>
                             </div>
                         </div>
                    </motion.div>
                </div>
            </div>
        </motion.div>
      </div>
    </section>
  );
};
